import React, { useEffect, useState } from "react";
import profileConfig from "../../config/forms/profile.json";
import { IMAGES } from "../../constants/image";
import { useNavigate } from "react-router-dom";
import { ENDPOINTS } from "../../services/endpoints";
import { validateValue } from "../../utils/validation";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import PhoneNumberInput from "../common/PhoneNumberInput";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";
interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
}
interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  validation?: FieldValidation;
  options?: { label: string; value: string }[];
  visibleWhen?: {
    field: string;
    values?: string[];
  };
  dynamicPlaceholder?: Record<string, string>;
}
const Profile: React.FC = () => {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showPopup = usePopup();
  const { userDetails, updateUserDetails } = useAuth();
  const fields = profileConfig.fields as FormField[];
  const ICON_MAP: Record<string, string> = {
    name: IMAGES.ICONS.NAME,
    email: IMAGES.ICONS.EMAIL,
    contactNumber: IMAGES.ICONS.PHONE,
    profession: IMAGES.ICONS.PORTFOLIO,
    description: IMAGES.ICONS.DESCRIPTION,
  };

  //  VALIDATION
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      if (!isFieldVisible(field)) return;

      const value = formData[field.name];

      const error = validateValue(value, {
        label: field.label,
        ...(field.validation || {}),
      });

      if (error) newErrors[field.name] = error;
    });

    return newErrors;
  };

  const isFieldVisibleForData = (field: FormField, data: any) => {
    if (!field.visibleWhen) return true;

    const value = data[field.visibleWhen.field];
    if (!value) return false;

    if (field.visibleWhen.values) {
      return field.visibleWhen.values.includes(value);
    }

    return true;
  };

  const validateField = (name: string, value: string, data = formData) => {
    const field = fields.find((f) => f.name === name);
    if (!field || !isFieldVisibleForData(field, data)) return "";

    const error = validateValue(value, {
      label: field.label,
      ...(field.validation || {}),
    });

    return error || "";
  };
  //  HANDLE CHANGE
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const nextFormData = {
      ...formData,
      [name]: value,
      ...(name === "profession" && formData.profession !== value
        ? { description: "" }
        : {}),
    };

    setFormData(nextFormData);

    setErrors((prev: any) => ({
      ...prev,
      [name]: validateField(name, value, nextFormData),
      ...(name === "profession" ? { description: "" } : {}),
    }));
  };

  const handlePhoneChange = (
    name: string,
    value: string,
    countryCode: string,
  ) => {
    const nextFormData = {
      ...formData,
      [name]: value,
      contactCountryCode: countryCode,
    };

    setFormData(nextFormData);

    setErrors((prev: any) => ({
      ...prev,
      [name]: validateField(name, value, nextFormData),
    }));
  };

  useEffect(() => {
    if (!userDetails) return;

    setFormData({
      name: userDetails?.name || "",
      email: userDetails?.email || "",
      contactNumber:
        userDetails?.contactNumber || userDetails?.contact_no || "",
      contactCountryCode:
        userDetails?.contactCountryCode || userDetails?.country_code || "+91",
      profession: userDetails?.profession || userDetails?.occupation || "",
      description:
        userDetails?.description ||
        userDetails?.occupation_description ||
        userDetails?.institution_name ||
        "",
    });
  }, [userDetails]);

  //Visibility Check
  const isFieldVisible = (field: FormField) => {
    return isFieldVisibleForData(field, formData);
  };

  //Dynamic Placeholder
  const getPlaceholder = (field: FormField) => {
    if (field.dynamicPlaceholder) {
      const selected = formData["profession"];
      return field.dynamicPlaceholder[selected] || field.placeholder;
    }
    return field.placeholder;
  };

  //Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    console.log("Form Data:", formData);
    // Map payload
    const data = {
      name: formData.name,
      email: formData.email,
      country_code: formData.contactCountryCode || "+91",
      contact_no: formData.contactNumber,
      occupation: formData.profession,
      description: formData.description,
    };

    try {
      const response = await api.post(ENDPOINTS.PROFILE.UPDATE, data);
      console.log("Profile Success:", response.data);
      const apiData = response?.data;
      const messageText =
        typeof apiData?.message === "string" ? apiData.message : "";
      const messageResponse = extractMessageResponse(apiData, "Profile updated successfully.", 200);
      const responseError =
        apiData?.error?.[0] ||
        apiData?.message?.error?.[0] ||
        (apiData?.status === false ? apiData?.message : "") ||
        (/unauthenticated|invalid|error|failed/i.test(messageText)
          ? messageText
          : "") ||
        (typeof apiData?.message === "string" &&
        apiData?.message.toLowerCase().includes("already registered")
          ? apiData?.message
          : "");

      if (responseError) {
        setErrors((prev: any) => ({
          ...prev,
          apiError: responseError,
        }));
        showPopup({
          status: messageResponse.status,
          message: responseError,
          messageType: messageResponse.messageType,
          messageCode: messageResponse.messageCode,
        });
      } else {
        const updatedUserDetails = {
          ...(userDetails || {}),
          ...(apiData?.userDetails || apiData?.data || {}),
          name: formData.name,
          email: formData.email,
          contactNumber: formData.contactNumber,
          contact_no: formData.contactNumber,
          contactCountryCode: formData.contactCountryCode || "+91",
          country_code: formData.contactCountryCode || "+91",
          profession: formData.profession,
          occupation: formData.profession,
          description: formData.description,
        };
        updateUserDetails(updatedUserDetails);
        showPopup({
          status: messageResponse.status,
          message: messageResponse.message,
          messageType: messageResponse.messageType,
          messageCode: messageResponse.messageCode,
          onOk: () => navigate("/"),
        });
      }
    } catch (error: any) {
      const apiError = error?.response?.data;
      const messageResponse = extractMessageResponse(apiError, "Profile update failed", 500);
      const message = messageResponse.message;

      setErrors((prev: any) => ({
        ...prev,
        apiError: message,
      }));
      showPopup({
        status: messageResponse.status,
        message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancel = () => {
    navigate("/");
  };
  return (
    <div className="profile-wrapper">
      <div className="profile-background">
        <h3 className="title text-center mb-3">Profile</h3>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => {
            if (!isFieldVisible(field)) return null;

            const value = formData[field.name] || "";
            const error = errors[field.name];

            return (
              <div className="mb-3 position-relative" key={field.name}>
                {/* Label */}
                {field.label && (
                  <label className="form-label">{field.label}</label>
                )}

                {/* Input / Select */}
                <div className="signup-input-group">
                  {field.type === "select" ? (
                    <select
                      name={field.name}
                      className={`form-select custom-input ${error ? "is-invalid" : ""}`}
                      value={value}
                      onChange={handleChange}
                    >
                      <option value="">{field.placeholder || "Select"}</option>

                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : field.name === "contactNumber" ? (
                    <PhoneNumberInput
                      name={field.name}
                      value={value}
                      countryCode={formData.contactCountryCode}
                      placeholder={getPlaceholder(field)}
                      hasError={Boolean(error)}
                      onChange={handlePhoneChange}
                    />
                  ) : (
                    <input
                      type={field.type}
                      name={field.name}
                      className={`form-control custom-input ${error ? "is-invalid" : ""}`}
                      placeholder={getPlaceholder(field)}
                      value={value}
                      onChange={handleChange}
                    />
                  )}

                  {/* Icon */}
                  {ICON_MAP[field.name] && field.name !== "contactNumber" && (
                    <img
                      src={ICON_MAP[field.name]}
                      alt={field.name}
                      className="input-icon"
                    />
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="invalid-feedback d-block">{error}</div>
                )}
              </div>
            );
          })}

          {/* Buttons */}

          {/* <button type="submit" className="btn btn-primary">
            Update Profile
          </button> */}
          <div className="d-flex gap-2 signup-action-row">
            <button
              type="submit"
              className="btn btn-primary w-100 signup-action-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Update Profile"}
            </button>
            <button
              type="button"
              className="btn btn-secondary w-100 signup-action-btn"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default Profile;
